import { formatMoney as sharedFormatMoney } from '@shared/money';

/**
 * Formats integer minor units (cents) as a currency string. Nullish values
 * render as an em dash so missing money never silently reads as zero.
 */
export function formatMoney(minor: number | null | undefined, currency = 'USD'): string {
  if (minor === null || minor === undefined || Number.isNaN(minor)) return '—';
  return sharedFormatMoney(minor, currency);
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * `YYYY-MM-DD` (or an ISO timestamp) to a friendly date such as `14 Mar 2026`.
 * Plain date strings are read as calendar dates so they never shift timezones.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    const monthName = MONTHS[Number(month) - 1] ?? month;
    return `${Number(day)} ${monthName} ${year}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return `${parsed.getUTCDate()} ${MONTHS[parsed.getUTCMonth()] ?? ''} ${parsed.getUTCFullYear()}`.trim();
}

/**
 * ISO-8601 UTC timestamp to `14 Mar 2026, 11:32 CAT`. Storage is UTC; display
 * is Central Africa Time (UTC+2, no daylight saving), the school's timezone.
 */
const CAT_OFFSET_MS = 2 * 60 * 60 * 1000;

export function formatDateTime(value: string | null | undefined, options?: { seconds?: boolean }): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const cat = new Date(parsed.getTime() + CAT_OFFSET_MS);
  const date = `${cat.getUTCDate()} ${MONTHS[cat.getUTCMonth()] ?? ''} ${cat.getUTCFullYear()}`.trim();
  const hours = String(cat.getUTCHours()).padStart(2, '0');
  const minutes = String(cat.getUTCMinutes()).padStart(2, '0');
  if (options?.seconds) {
    const seconds = String(cat.getUTCSeconds()).padStart(2, '0');
    return `${date}, ${hours}:${minutes}:${seconds} CAT`;
  }
  return `${date}, ${hours}:${minutes} CAT`;
}

/** Up to two uppercase initials from a full name, e.g. "Tendai Moyo" -> "TM". */
export function initials(fullName: string | null | undefined): string {
  if (!fullName) return '?';
  const parts = fullName
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);
  if (parts.length === 0) return '?';
  const first = parts[0];
  const last = parts.length > 1 ? parts[parts.length - 1] : undefined;
  const letters = `${first?.[0] ?? ''}${last?.[0] ?? ''}`;
  return letters.toUpperCase() || '?';
}

/** "1 student", "2 students", "3 children". */
export function pluralise(count: number, singular: string, plural?: string): string {
  const word = count === 1 ? singular : (plural ?? `${singular}s`);
  return `${count.toLocaleString('en-US')} ${word}`;
}

export interface CsvColumn {
  key: string;
  label: string;
}

function csvEscape(value: string): string {
  if (/["\n\r,]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Convert table rows into CSV text (RFC 4180, CRLF line endings, quoted when
 * needed). Values are stringified; null/undefined become empty cells.
 */
export function csvToString(
  rows: readonly Record<string, unknown>[],
  columns: readonly CsvColumn[],
): string {
  const header = columns.map((column) => csvEscape(column.label)).join(',');
  const lines = rows.map((row) =>
    columns
      .map((column) => {
        const value = row[column.key];
        if (value === null || value === undefined) return '';
        return csvEscape(String(value));
      })
      .join(','),
  );
  return [header, ...lines].join('\r\n');
}

/** Trigger a browser download for a Blob (used by CSV/report exports). */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Integer cents to a plain decimal string for editing in MoneyInput, e.g. 12345 -> "123.45". */
export function centsToDecimalString(minor: number | null | undefined): string {
  if (minor === null || minor === undefined || Number.isNaN(minor)) return '';
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(Math.round(minor));
  const whole = Math.floor(abs / 100);
  const cents = String(abs % 100).padStart(2, '0');
  return `${sign}${whole}.${cents}`;
}

/** Whether the user asked the OS to reduce motion (used by toasts/animations). */
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
